import React, { useState } from "react";
import { X, FileText, Plus, Edit2, Trash2, Save, X as CloseIcon } from "lucide-react";

const UploadLyricsPopup = ({ order, onClose, onUpload }) => {
  const [lyricsText, setLyricsText] = useState("");
  const [lyricsList, setLyricsList] = useState([]);
  const [editingLyricId, setEditingLyricId] = useState(null);
  const [editText, setEditText] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleAddLyrics = () => {
    if (!lyricsText.trim()) return;

    const newLyric = {
      text: lyricsText.trim(),
    };

    setLyricsList(prev => [...prev, newLyric]);
    setLyricsText("");
  };

  const handleEditLyrics = (lyric) => {
    setEditingLyricId(lyric.id);
    setEditText(lyric.text);
  };

  const handleSaveEdit = () => {
    if (!editText.trim()) return;

    setLyricsList(prev =>
      prev.map(lyric =>
        lyric.id === editingLyricId
          ? {
              ...lyric,
              text: editText.trim(),
              updatedAt: new Date().toISOString()
            }
          : lyric
      )
    );
    setEditingLyricId(null);
    setEditText("");
  };

  const handleCancelEdit = () => {
    setEditingLyricId(null);
    setEditText("");
  };

  const handleDeleteLyrics = (lyricId) => {
    setLyricsList(prev => prev.filter(lyric => lyric.id !== lyricId));
    if (editingLyricId === lyricId) {
      setEditingLyricId(null);
      setEditText("");
    }
  };

  const handleUpload = async () => {
    if (lyricsList.length === 0) return;

    // setUploading(true);
    // try {
    //   // Simulate upload process
    //   await new Promise(resolve => setTimeout(resolve, 1500));
      
    //   // Here you would typically send to your backend
    //   const uploadedLyrics = lyricsList.map(lyric => ({
    //     ...lyric,
    //     orderId: order.id,
    //     status: 'uploaded'
    //   }));

    //   onUpload(uploadedLyrics);
    //   onClose();
    // } catch (error) {
    //   console.error("Upload failed:", error);
    // } finally {
    //   setUploading(false);
    // }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Add Lyrics
              </h3>
              <p className="text-sm text-gray-600">
                For {order.companyName || order.company_name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Input Section */}
          <div className="space-y-4">
            <label className="text-sm font-medium text-gray-900">
              Add New Lyrics
            </label>
            <div className="space-y-3">
              <textarea
                value={lyricsText}
                onChange={(e) => setLyricsText(e.target.value)}
                placeholder="Paste or type lyrics here... You can add multiple versions of lyrics"
                rows={6}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  {lyricsText.length} characters
                </span>
                <button
                  onClick={handleAddLyrics}
                  disabled={!lyricsText.trim()}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Lyrics
                </button>
              </div>
            </div>
          </div>

          {/* Lyrics List Section */}
          {lyricsList.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  Added Lyrics ({lyricsList.length})
                </h4>
                <span className="text-xs text-gray-500">
                  Click edit to modify any lyrics
                </span>
              </div>

              <div className="space-y-4">
                {lyricsList.map((lyric, index) => (
                  <div
                    key={lyric.id}
                    className="border border-gray-200 rounded-lg bg-gray-50/50"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">
                          Lyrics Version {index + 1}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(lyric.updatedAt)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingLyricId !== lyric.id ? (
                          <>
                            <button
                              onClick={() => handleEditLyrics(lyric)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit lyrics"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLyrics(lyric.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete lyrics"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={handleSaveEdit}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Save changes"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Cancel edit"
                            >
                              <CloseIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      {editingLyricId === lyric.id ? (
                        <div className="space-y-3">
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          />
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              {editText.length} characters
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveEdit}
                                disabled={!editText.trim()}
                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Save Changes
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                          {lyric.text}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {lyricsList.length === 0 && (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                No lyrics added yet. Paste your lyrics above and click "Add Lyrics".
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={lyricsList.length === 0 || uploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving Lyrics...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                Save Lyrics
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadLyricsPopup;